import React, { useState, useEffect, useRef } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IconButton, Grid } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import PieChartOutlineRoundedIcon from '@mui/icons-material/PieChartOutlineRounded';
import { downloadFile } from '#action/download';
import DeviceProjectsTreePopup from '#component/device/dashboard/projects-tree';
import UnfoldableCard from '#component/device/general/unfoldable-card/unfoldable-card';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu';
import propTypes from './prop-types';
import { CardsAlignment, MobileTitle, StyledPagination, LinkShowAll } from './styles';

const DeviceListMobile = props => {
  const history = useHistory();
  const dispatch = useDispatch();
  const scrollableContainerRef = useRef(null);
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [selectedDeviceId, setSelectedDeviceId] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const meta = useSelector(s => s.meta, shallowEqual);
  const { account, devices, closeList, uids } = props;

  const pageCount = Math.ceil(devices.length / pageSize);
  const indexOfLastDevice = currentPage * pageSize;
  const indexOfFirstDevice = indexOfLastDevice - pageSize;
  const currentDevices = devices.slice(indexOfFirstDevice, indexOfLastDevice);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleShowDetailsPopup = device => {
    setShowDetailsDialog(true);
    setSelectedDevice(device);
  };

  const handleDownloadQRClick = async (e, deviceId) => {
    if (deviceId) {
      const qrDevice = devices.find(device => device.id === selectedDeviceId);
      e.stopPropagation();
      const path = `report/device/${qrDevice.id}/label`;
      const fileName = `Label ${qrDevice.title || qrDevice.uid}.pdf`;

      await dispatch(downloadFile(
        path,
        fileName
      ));
    }
  };

  const menuItems = [
    { name: 'Metrics and events', onClick: () => history.push(`/device/${selectedDeviceId}`), icon: <PieChartOutlineRoundedIcon /> },
    { name: 'Details', onClick: e => handleShowDetailsPopup(e, selectedDevice), icon: <AccountTreeOutlinedIcon /> },
    { name: 'Download QR', onClick: e => handleDownloadQRClick(e, selectedDeviceId), icon: <WidgetsOutlinedIcon /> }
  ];

  useEffect(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  const findSelectedDevice = clickedDeviceId =>
    devices.find(device => device.id === clickedDeviceId);

  return (
    <>
      <CardsAlignment ref={scrollableContainerRef}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2} textAlign="left">
            <IconButton
              aria-label="Close"
              onClick={() => closeList([])}
            >
              <ArrowBackIosIcon />
            </IconButton>
          </Grid>
          <Grid item xs={8} textAlign="center">
            <MobileTitle>
              Devices (
              {devices.length}
              )
            </MobileTitle>
          </Grid>
          <Grid item xs={2} />
        </Grid>
        {currentDevices.map(device => (
          <UnfoldableCard
            key={device.id}
            device={device}
            account={account}
            onOpenDrawer={deviceId => {
              setIsDrawerMenuOpen(true);
              setSelectedDeviceId(deviceId);
            }}
            meta={meta}
          />
        ))}
        {pageCount > 1 && (
          <StyledPagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="secondary"
          />
        )}
        {uids?.length > 100 ?
          <LinkShowAll
            to="/device"
          >
            <span>Show all devices</span>
          </LinkShowAll> : null}
      </CardsAlignment>
      <MobileDrawerMenu
        isOpen={isDrawerMenuOpen}
        onClose={setIsDrawerMenuOpen}
        menuTitle={devices.find(device =>
          device.id === selectedDeviceId)?.title}
        menuItems={menuItems}
      />
      {showDetailsDialog && <DeviceProjectsTreePopup
        open={showDetailsDialog}
        setOpen={setShowDetailsDialog}
        deviceUid={selectedDevice.uid}
        deviceTitle={selectedDevice.title}
        device={findSelectedDevice(selectedDeviceId)}
        setSelectedDevice={setSelectedDevice}
      />}
    </>
  );
};

DeviceListMobile.propTypes = propTypes;

export default DeviceListMobile;
